import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "@mantine/form";
import {
     TextInput,
     PasswordInput,
     Button,
     Stack,
     Text,
     Anchor,
     Alert,
} from "@mantine/core";
import { STORAGE_TOKEN_KEY, useAuth } from "../../contexts/AuthContext";
import { useToastNotification } from "../../hooks/useToastNotification";
import {
     IconMail,
     IconLock,
     IconPhone,
     IconCheck,
     IconX,
} from "@tabler/icons-react";
import { HttpCodes, Logo, AppName, AppShortName } from "../../lib/data";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { NonPaginatedResponse } from "../../types";
import { IUserProps } from "../../types/User";

import "./styles.scss";

export default function Register() {
     const navigate = useNavigate();
     const [loading, setLoading] = useState(false);
     const [error, setError] = useState<string | null>(null);
     const { reloadUser } = useAuth();
     const { success, error: showError } = useToastNotification();
     const [passwordFocused, setPasswordFocused] = useState(false);

     const registerForm = useForm({
          initialValues: {
               email: "",
               phone: "",
               password: "",
               confirmPassword: "",
          },
          validate: {
               email: (value) =>
                    /^\S+@\S+$/.test(value) ? null : "Invalid email",
               phone: (value) => {
                    if (!value) return "Phone number is required";
                    const digits = value.replace(/\D/g, "");
                    if (digits.length !== 11) {
                         return "Phone number must be exactly 11 digits";
                    }
                    if (!/^\d/.test(digits)) {
                         return "Phone number must start with a number";
                    }
                    return null;
               },
               password: (value) => {
                    if (!value) return "Password is required";
                    if (value.length < 8)
                         return "Password must be at least 8 characters";
                    if (!/[a-z]/.test(value))
                         return "Password must contain at least one lowercase letter";
                    if (!/[A-Z]/.test(value))
                         return "Password must contain at least one uppercase letter";
                    if (!/[0-9]/.test(value))
                         return "Password must contain at least one number";
                    if (!/[^A-Za-z0-9]/.test(value))
                         return "Password must contain at least one special character";
                    return null;
               },
               confirmPassword: (value, values) =>
                    value !== values.password ? "Passwords do not match" : null,
          },
     });

     // Password requirements checker
     const checkPasswordRequirements = (password: string) => {
          return {
               minLength: password.length >= 8,
               hasLowercase: /[a-z]/.test(password),
               hasUppercase: /[A-Z]/.test(password),
               hasNumber: /[0-9]/.test(password),
               hasSpecial: /[^A-Za-z0-9]/.test(password),
          };
     };

     const passwordRequirements = checkPasswordRequirements(
          registerForm.values.password
     );

     const handleSubmit = async () => {
          setError(null);
          const { hasErrors } = registerForm.validate();
          if (!hasErrors) {
               try {
                    setLoading(true);
                    const { email, phone, password } = registerForm.values;
                    const r = await PerformRequest<
                         NonPaginatedResponse<{
                              token: string;
                              user: IUserProps;
                         }>
                    >({
                         method: Endpoints.GetRegister().method,
                         route: Endpoints.GetRegister().route,
                         data: {
                              email,
                              phone,
                              password,
                         },
                         doNotUseAuthorization: true,
                    });
                    if (r) {
                         if (
                              r.status === HttpCodes.Success ||
                              r.status === HttpCodes.Created
                         ) {
                              success("Registration successful!");
                              navigate("/login", {
                                   state: {
                                        message: "Registration successful!",
                                   },
                              });
                         } else {
                              const errorMessage =
                                   r.message || "Registration failed";
                              setError(errorMessage);
                              showError(errorMessage);
                         }
                    }
               } catch (err: any) {
                    const errorMessage = err.message || "Registration failed";
                    setError(errorMessage);
                    showError(errorMessage);
               } finally {
                    setLoading(false);
               }
          }
     };

     return (
          <div className="min-h-screen flex">
               {/* Register Form */}
               <div className="w-full flex items-center justify-center p-8 bg-white register-container">
                    <div className="w-full max-w-md">
                         {/* Logo */}
                         <div className="flex justify-center mb-8">
                              <img
                                   src={Logo}
                                   alt={`${AppName} Logo`}
                                   className="h-16"
                              />
                         </div>

                         {/* Header */}
                         <div className="text-center mb-10">
                              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                                   Welcome to {AppShortName}
                              </h1>
                              <p className="text-gray-500 text-base">
                                   Create your account and start your financial
                                   journey
                              </p>
                         </div>

                         {/* Error Alert */}
                         {error && (
                              <div className="mb-6">
                                   <Alert color="red" title="Error">
                                        {error}
                                   </Alert>
                              </div>
                         )}

                         {/* Form */}
                         <form
                              onSubmit={(e) => {
                                   e.preventDefault();
                                   registerForm.onSubmit(handleSubmit)();
                              }}
                         >
                              <Stack gap="md">
                                   {/* Email Input */}
                                   <TextInput
                                        {...registerForm.getInputProps("email")}
                                        placeholder="Email"
                                        leftSection={
                                             <IconMail size={20} stroke={1.5} />
                                        }
                                        size="md"
                                        styles={{
                                             input: {
                                                  borderRadius: "4px",
                                             },
                                        }}
                                   />

                                   {/* Phone Input */}
                                   <TextInput
                                        name="phone"
                                        {...registerForm.getInputProps("phone")}
                                        placeholder="01234567890"
                                        leftSection={
                                             <IconPhone
                                                  size={20}
                                                  stroke={1.5}
                                             />
                                        }
                                        size="md"
                                        onChange={(e) => {
                                             // Only allow numbers
                                             let value = e.target.value.replace(
                                                  /\D/g,
                                                  ""
                                             );

                                             // Limit to 11 digits
                                             if (value.length > 11) {
                                                  value = value.slice(0, 11);
                                             }

                                             registerForm.setFieldValue(
                                                  "phone",
                                                  value
                                             );
                                        }}
                                        styles={{
                                             input: {
                                                  borderRadius: "4px",
                                             },
                                        }}
                                   />

                                   {/* Password Input */}
                                   <div>
                                   <PasswordInput
                                        {...registerForm.getInputProps(
                                             "password"
                                        )}
                                        placeholder="Password"
                                        leftSection={
                                                  <IconLock
                                                       size={20}
                                                       stroke={1.5}
                                                  />
                                        }
                                        size="md"
                                             onFocus={() =>
                                                  setPasswordFocused(true)
                                             }
                                             onBlur={() =>
                                                  setPasswordFocused(false)
                                             }
                                        styles={{
                                             input: {
                                                  borderRadius: "4px",
                                             },
                                        }}
                                   />
                                        {/* Password Requirements */}
                                        {(passwordFocused ||
                                             registerForm.values.password) && (
                                             <div className="password-requirements">
                                                  <div className="requirements-title">
                                                       Password Requirements:
                                                  </div>
                                                  <div className="requirements-list">
                                                       <div
                                                            className={`requirement-item ${
                                                                 passwordRequirements.minLength
                                                                      ? "met"
                                                                      : ""
                                                            }`}
                                                       >
                                                            {passwordRequirements.minLength ? (
                                                                 <IconCheck
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            ) : (
                                                                 <IconX
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            )}
                                                            <span>
                                                                 At least 8
                                                                 characters
                                                            </span>
                                                       </div>
                                                       <div
                                                            className={`requirement-item ${
                                                                 passwordRequirements.hasUppercase
                                                                      ? "met"
                                                                      : ""
                                                            }`}
                                                       >
                                                            {passwordRequirements.hasUppercase ? (
                                                                 <IconCheck
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            ) : (
                                                                 <IconX
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            )}
                                                            <span>
                                                                 One uppercase
                                                                 letter
                                                            </span>
                                                       </div>
                                                       <div
                                                            className={`requirement-item ${
                                                                 passwordRequirements.hasLowercase
                                                                      ? "met"
                                                                      : ""
                                                            }`}
                                                       >
                                                            {passwordRequirements.hasLowercase ? (
                                                                 <IconCheck
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            ) : (
                                                                 <IconX
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            )}
                                                            <span>
                                                                 One lowercase
                                                                 letter
                                                            </span>
                                                       </div>
                                                       <div
                                                            className={`requirement-item ${
                                                                 passwordRequirements.hasNumber
                                                                      ? "met"
                                                                      : ""
                                                            }`}
                                                       >
                                                            {passwordRequirements.hasNumber ? (
                                                                 <IconCheck
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            ) : (
                                                                 <IconX
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            )}
                                                            <span>
                                                                 One number
                                                            </span>
                                                       </div>
                                                       <div
                                                            className={`requirement-item ${
                                                                 passwordRequirements.hasSpecial
                                                                      ? "met"
                                                                      : ""
                                                            }`}
                                                       >
                                                            {passwordRequirements.hasSpecial ? (
                                                                 <IconCheck
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            ) : (
                                                                 <IconX
                                                                      size={16}
                                                                      stroke={
                                                                           2.5
                                                                      }
                                                                 />
                                                            )}
                                                            <span>
                                                                 One special character
                                                            </span>
                                                       </div>
                                                  </div>
                                             </div>
                                        )}
                                   </div>

                                   {/* Confirm Password Input */}
                                   <PasswordInput
                                        {...registerForm.getInputProps(
                                             "confirmPassword"
                                        )}
                                        placeholder="Confirm Password"
                                        leftSection={
                                             <IconCheck
                                                  size={20}
                                                  stroke={1.5}
                                             />
                                        }
                                        size="md"
                                        styles={{
                                             input: {
                                                  borderRadius: "4px",
                                             },
                                        }}
                                   />

                                   {/* Register Button */}
                                   <Button
                                        type="submit"
                                        fullWidth
                                        size="md"
                                        loading={loading}
                                        styles={{
                                             root: {
                                                  background:
                                                       "linear-gradient(135deg, #699269 0%, #558B2F 100%)",
                                                  border: "none",
                                                  borderRadius: "4px",
                                                  height: "48px",
                                                  boxShadow:
                                                       "0 2px 8px rgba(105, 146, 105, 0.25)",
                                             },
                                        }}
                                   >
                                        Sign Up
                                   </Button>
                              </Stack>
                         </form>

                         {/* Login Link */}
                         <div className="mt-8 text-center">
                              <Text size="sm" c="dimmed">
                                   Already have an account?{" "}
                                   <Anchor
                                        component={Link}
                                        to="/login"
                                        fw={600}
                                        style={{ color: "#699269" }}
                                   >
                                        Login
                                   </Anchor>
                              </Text>
                         </div>
                    </div>
               </div>
          </div>
     );
}
